import React, { useContext, useEffect, useMemo, useRef } from 'react';
import RecorderDrawerContext from '../../Context';
import {
  Carousel,
  CarouselHookProps,
  CarouselItem,
  useCarousel,
} from '@vimily/bonjoro-ui-components-v2/Carousel';
import { VideoPlayer, VideoPlayerPlayPausePlacement } from '@vimily/bonjoro-ui-components-v2/VideoPlayer';
import { RedoButton } from './RedoButton';
import { TabButtons } from './TabButtons';
import { LandingPagePreview } from './LandingPagePreview';
import { PreviewTypeDetail } from './PreviewDetails';
import { flow } from 'lodash';
import {
  buildTemplateProgram,
  templateProgramToReact,
  templateProgramToString,
} from '~/utils/templating/compiler';
import { templateData } from '~/App/Modules/Settings/MessageTemplates/Pages/Editor';
import { EmailPreview } from './EmailPreview';
import { useFeatures } from '~/react-hooks/use-features';
import { List, Map } from 'immutable';
import { insertEmbedItem } from '~/App/Modules/Settings/MessageTemplates/Components/Tabs/ContentBlock';
import { PreviewProps } from '~/App/Modules/Settings/MessageTemplates/Pages/Editor/LandingPagePreview';
import { assetUrl } from '~/helpers';
import { getProfileName } from '~/BonjorosList/components/Result/helpers';
import { getProfileFirstName } from '~/BonjorosList/util';
import useCurrentUser from '~/react-hooks/use-current-user';
import { PreviewComponentProps } from '~/App/Modules/Settings/MessageTemplates/Pages/Editor/EmailTemplatePreview';
import { SendingPanel } from './SendingPanel';
import { UploadingScreen } from './UploadingScreen';
import { MessageTemplateType } from '~/types/models';
import { ThumbnailType } from '@vimily/bonjoro-ui-components-v2/ThumbnailPicker';
import { usePreviewTabIndex } from '~/react-hooks/use-preview-tab-index';

const defaultPlayerImage = 'https://loremflickr.com/200/200/portrait';

export interface CompiledTemplatesProps {
  subjectLine: string;
  message: React.ReactNode;
}

export enum PreviewTabIndex {
  Record = 0,
  Email = 1,
  LandingPage = 2,
}

const taskRecorderNavDefaultClassName =
  'w-14 h-[60px] p-0.5 rounded-full hover:v2-bg-yellow-100 v2-bg-white/50 v2-transition-colors';

export function Preview() {
  const {
    message,
    messageTemplate,
    videoUrl,
    hasValidVideo,
    selectedThumbnail,
    isUploading,
    onRecorderPlay,
    profiles,
    defaultMessage,
    defaultSubjectLine,
    emailPreviewDisclaimerContent,
    emailPreviewBannerContent,
  } = useContext(RecorderDrawerContext);

  const onPlayClick = () => {
    onRecorderPlay?.();
  };

  const { index, setIndex } = usePreviewTabIndex(PreviewTabIndex.Record);
  const carouselRef = useRef<HTMLUListElement>(null);
  const { setActiveIndex, activeIndex, setToFirst, setToLast, items } =
    useCarousel(carouselRef);

  const onActiveIndexChange = (index: number) => {
    setActiveIndex(index);
    setIndex(index);
  };

  const carouselProps: CarouselHookProps = {
    activeIndex,
    setActiveIndex: onActiveIndexChange,
    setToNext: () => onActiveIndexChange(activeIndex + 1),
    setToPrev: () => onActiveIndexChange(activeIndex - 1),
    setToFirst,
    setToLast,
    items,
  };

  useEffect(() => {
    carouselProps?.setActiveIndex(index);
  }, [carouselProps, index]);

  const profile = profiles?.[0] ?? {
    email: 'joro@bonjoro.com',
    first_name: 'Joro',
    last_name: 'Bonjoro',
    name: 'Joro Bonjoro',
  };
  const user = useCurrentUser();

  const { canAccessNextGenTemplate, shouldHideBonjoroBranding } = useFeatures();

  const compiledTemplates = useMemo<CompiledTemplatesProps>(() => {
    const inputData =
      profiles != undefined && profiles.length > 1
        ? templateData
        : {
            email: profile?.email,
            first_name: profile?.first_name,
            last_name: profile?.last_name,
            full_name: profile?.name,
          };

    const compileStringTemplate = flow(
      buildTemplateProgram,
      templateProgramToString(inputData)
    );
    const compileReactTemplate = flow(
      buildTemplateProgram,
      templateProgramToReact(inputData)
    );

    return {
      subjectLine: compileStringTemplate(
        messageTemplate?.email_template?.subject_line ||
          defaultSubjectLine ||
          ''
      ),
      message: compileReactTemplate(message || defaultMessage || ''),
    };
  }, [
    profiles,
    profile,
    messageTemplate,
    message,
    defaultSubjectLine,
    defaultMessage,
  ]);

  const embedsList = useMemo<List<Map<string, string>>>(() => {
    if (!messageTemplate?.embeds) return List<Map<string, string>>();

    let items = List<Map<string, string>>();
    messageTemplate.embeds?.forEach((embed) => {
      items = insertEmbedItem(embed, items);
    });

    return items;
  }, [messageTemplate?.embeds]);

  const thumbnailType = useMemo(() => {
    if (messageTemplate?.template_type === MessageTemplateType.NextGen) {
      return ThumbnailType.Original;
    }

    return ThumbnailType.Square;
  }, [messageTemplate?.template_type]);

  const landingPageProps = {
    ctaUrl:
      messageTemplate?.landing_page?.cta?.uri ??
      window.Bonjoro?.messageTemplate?.defaultCtaUrl ??
      '',
    ctaText:
      messageTemplate?.landing_page?.cta?.text ??
      window.Bonjoro?.messageTemplate?.defaultCtaText ??
      '',
    showCta:
      messageTemplate?.landing_page?.cta?.is_displayed ??
      window.Bonjoro?.messageTemplate?.defaultIsCtaDisplayed ??
      false,
    color: messageTemplate?.theme?.primary_color ?? '#fc5a00',
    secondaryColor: messageTemplate?.theme?.secondary_color ?? '#ffffff',
    userImage:
      user.image ??
      assetUrl('/assets/images/message-template/default-preview.jpg'),
    userName: getProfileName(user),
    userShortName: (getProfileFirstName(user) as string) ?? '',
    playerImage:
      `"${selectedThumbnail?.[thumbnailType].data ?? ''}"` ||
      defaultPlayerImage,
    backgroundUrl: messageTemplate?.landing_page?.background?.url,
    logoUrl:
      messageTemplate?.landing_page?.logo?.url ||
      assetUrl('/assets/images/logos/logo_full.png'),
    message: compiledTemplates.message,
    showBonjoroBadge: !shouldHideBonjoroBranding,
    showSocialSharing: messageTemplate?.is_greet_shareable || false,
    embeds: embedsList,
    templateType:
      messageTemplate?.template_type || MessageTemplateType.Postcard,
    canAccessNextGenTemplate,
    hidePreviewTag: true,
  } as PreviewProps;

  const emailProps = {
    name: landingPageProps?.userName,
    bio: user?.bio ?? '',
    shortName: landingPageProps?.userShortName,
    subject: compiledTemplates.subjectLine,
    message: compiledTemplates.message,
    image: landingPageProps.userImage,
    playerImage: selectedThumbnail?.square?.data || defaultPlayerImage,
    theme: {
      primaryColor: landingPageProps?.color,
      secondaryColor: landingPageProps?.secondaryColor,
    },
    logoUrl: messageTemplate?.landing_page?.logo?.url,
    hidePreviewTag: landingPageProps?.hidePreviewTag,
    bannerContent: emailPreviewBannerContent,
    disclaimerContent: emailPreviewDisclaimerContent,
  } as PreviewComponentProps;

  return (
    <>
      <div className='flex flex-row gap-x-4 items-center w-full px-4 pt-4 v2-animate-fade-in'>
        <i className='icon icon-preview !v2-bg-neutral-900' />
        <span className='text-[21px] leading-normal font-bold text-neutral-900'>
          Preview message and template
        </span>
      </div>
      <div className='relative mt-5 w-full h-full md:!w-auto md:!h-auto'>
        <Carousel
          {...carouselProps}
          containerClassName='w-full h-full'
          overflowClassName='w-full h-full md:!w-[500px] md:!h-[400px]'
          carouselRef={carouselRef}
          hideBonjoroBadge={true}
          hideIndicators={true}
          hideNavigator={!hasValidVideo}
          leftNavigatorProps={{
            containerClassName: `!-left-4 justify-end ${taskRecorderNavDefaultClassName}`,
          }}
          rightNavigatorProps={{
            containerClassName: `!-right-4 justify-start ${taskRecorderNavDefaultClassName}`,
          }}
        >
          <CarouselItem innerClassName='flex flex-col items-center justify-center'>
            <VideoPlayer
              videoUrl={videoUrl}
              videoThumbnail={selectedThumbnail?.original.data}
              hideClose
              hideFullScreen
              hideNewTab
              hideLoader
              hideMuteUnmute
              showProgressBar
              onPlay={onPlayClick}
              forceFirstFrame={false}
              videoClassName='!w-[400px] !h-[400px] rounded-2xl'
              containerClassName='py-2 rounded-2xl items-center justify-center'
              playPauseStyle={{ color: messageTemplate?.theme?.primary_color }}
              playPausePlacement={VideoPlayerPlayPausePlacement.Center}
            />
          </CarouselItem>
          <CarouselItem innerClassName='flex flex-col items-center justify-center'>
            <EmailPreview {...emailProps} />
          </CarouselItem>
          <CarouselItem innerClassName='flex flex-col items-center justify-center'>
            <LandingPagePreview {...landingPageProps} />
          </CarouselItem>
        </Carousel>
        <RedoButton />
      </div>
      <PreviewTypeDetail index={carouselProps?.activeIndex} />
      {carouselProps.items > 0 && <TabButtons carouselProps={carouselProps} />}
      <SendingPanel />
      {isUploading && <UploadingScreen />}
    </>
  );
}
